import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Stack, TextField, Button, Select, MenuItem } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationSnackbar, { NotificationType } from 'components/common/NotificationSnackBar';
import { listIssueCasts, type IssueCastListItem } from 'api/apiClient';
import IssueCastList from 'features/issuecasts/components/IssueCastList';

const IssueCastsPage: React.FC = () => {
  const [items, setItems] = useState<IssueCastListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [websiteIdFilter, setWebsiteIdFilter] = useState('');
  const [maxItems, setMaxItems] = useState<number>(100);

  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' as NotificationType });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listIssueCasts({
        websiteId: websiteIdFilter.trim() || undefined,
        maxItems,
      });
      setItems(data || []);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to load podcasts';
      setNotification({ open: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [websiteIdFilter, maxItems]);

  useEffect(() => {
    load();
  }, [load]);

  const closeNotification = () => setNotification({ ...notification, open: false });

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Podcasts
      </Typography>
      <Typography variant="body1" paragraph>
        Browse and preview podcasts across your websites. Use filters to narrow results.
      </Typography>

      <IssueCastList items={items} setItems={setItems} loading={loading} onIssueCastPublished={load} />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="Website ID (optional)"
            size="small"
            fullWidth
            value={websiteIdFilter}
            onChange={(e) => setWebsiteIdFilter(e.target.value)}
            placeholder="abc123"
          />
          <Select size="small" value={maxItems} onChange={(e) => setMaxItems(Number(e.target.value))}>
            <MenuItem value={50}>Max 50</MenuItem>
            <MenuItem value={100}>Max 100</MenuItem>
            <MenuItem value={200}>Max 200</MenuItem>
            <MenuItem value={500}>Max 500</MenuItem>
          </Select>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
    </Box>
  );
};

export default IssueCastsPage;
